import { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import ErrorBoundary from '@baaligames/shared-ui/components/ErrorBoundary';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import { useI18n } from '@baaligames/shared-ui/lib/i18n';
import { SUPPORTED_LANGS } from '@baaligames/shared-ui/lib/translations';
import type { Lang } from '@baaligames/shared-ui/lib/translations';
import NavBar from '@baaligames/shared-ui/components/NavBar';
import BottomNav from '@baaligames/shared-ui/components/BottomNav';
import OrientationPrompt from '@baaligames/shared-ui/components/OrientationPrompt';
import { useGameOrientationLock } from '@baaligames/shared-ui/lib/useGameOrientationLock';
import type { GameModule } from './games/types';
import { useNavigate } from 'react-router-dom';
import InstallPrompt from '@baaligames/shared-ui/lib/install';
import GeoForcedModal from './components/GeoForcedModal';
import { isGeoLocated } from '@baaligames/shared-ui/components/LocationCapture';
import CompleteProfileModal from './components/CompleteProfileModal';
import DistrictPicker from './components/DistrictPicker';
import RegisterPage from './pages/Register';
import LoginPage from './pages/Login';
import VerifyEmailPage from './pages/VerifyEmail';
import VerifyGatePage from './pages/Verify';
import ForgotPasswordPage from './pages/ForgotPassword';
import ResetPasswordPage from './pages/ResetPassword';
import ProfilePage from './pages/Profile';
import HomePage from './pages/Home';
import GamesPage from './pages/Games';
import { GAMES } from './games/registry';
import DevSandbox from './games/_devsandbox/Page';
import { useLobbyGames } from './lib/lobbyGames';
import WalletPage from './pages/Wallet';
import RewardsPage from './pages/Rewards';

// Mirrors the logged-in user's saved language preference into client
// i18n state so the same account speaks the same language on each
// device. Local explicit picks always win over the server-saved one.
function LanguageSync() {
  const { user } = useAuth();
  const { setLang } = useI18n();
  // 2026-08-08: the DB-saved language is the source of truth. Apply it ONCE
  // per login — so every fresh login/app-load opens the site in whatever the
  // user saved — while leaving TEMPORARY top-bar switches made during the
  // session untouched (this effect won't re-fire for the same logged-in user,
  // and it deliberately ignores localStorage so a temp choice on one device
  // never overrides the saved language on the next login).
  const appliedForRef = useRef<number | string | null>(null);
  useEffect(() => {
    if (!user) { appliedForRef.current = null; return; }
    const saved = (user as any).language as Lang | undefined;
    if (!saved || !SUPPORTED_LANGS.some((l) => l.code === saved)) return;
    const uid = (user as any).id ?? 'me';
    if (appliedForRef.current === uid) return; // already applied this login
    appliedForRef.current = uid;
    setLang(saved);
  }, [user, setLang]);
  return null;
}

// Auth gate — must be logged in to enter.
function Protected({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center text-neutral-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Task C (2026-05-10) — Verification gate. Wraps every authenticated
// surface that is NOT /verify itself. If the user is logged in but
// has neither a verified email nor a verified telegram, we redirect
// them to /verify. The gate page itself uses <Protected> only (no
// VerifiedRoute) so unverified users CAN reach it.
function VerifiedRoute({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center text-neutral-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // 2026-05-21 — Verification is no longer a hard gate on app access.
  // A signed-up but unverified player can use the full app immediately.
  // The /verify page stays reachable from Profile (and any banner we
  // add later), but we don't redirect anyone there involuntarily.
  // Reason: forced-verification was trapping real users (expired email
  // links, telegram setup failures, etc.) outside the app with a
  // working password but no way to "do" anything. Verification is now
  // a self-service security upgrade — primarily because telegram
  // verification is what powers password recovery.
  return children;
}

// Phase R7 follow-up (2026-05-13) — Home gate.
// `/` is a special case: HomePage internally renders two different
// experiences — a public marketing surface for logged-out visitors
// (LoggedOutHero / TrustRow / RewardsTeaserStrip / GameShowcase /
// LoggedOutCTA) and a personalised dashboard for logged-in players.
// Earlier the route was wrapped in <VerifiedRoute>, which kicked
// every logged-out visitor straight to /login and made the public
// marketing page unreachable — the code for it still existed but
// no one could see it.
// This gate restores access for logged-out visitors AND keeps the
// "unverified users must verify first" funnel intact:
//   - !user          → render <HomePage /> (public marketing branch)
//   - user, !verified → redirect to /verify
//   - user, verified  → render <HomePage /> (logged-in dashboard branch)
function HomeRoute({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center text-neutral-400">Loading…</div>;
  // 2026-05-21 — verification gate removed (see VerifiedRoute comment).
  // The dashboard renders for any logged-in user, verified or not.
  // The public homepage branch fires for `!user` exactly as before.
  return children;
}

/**
 * Per-game route wrapper. Reads the game's declared orientation from
 * its GameModule and:
 *
 *   • PORTRAIT game on LANDSCAPE device → wraps the Page in a
 *     CSS-rotated viewport (`.force-portrait`). The transform on the
 *     wrapper makes it the containing block for the Page's
 *     `position: fixed` root, so the game renders into a virtual
 *     portrait canvas (size = device's height × width, rotated 90°).
 *     The player keeps the phone in landscape but sees the game
 *     visually upright — no rotate-back prompt, no friction.
 *
 *   • LANDSCAPE game on PORTRAIT device → shows the rotate prompt
 *     instead. A landscape game NEEDS the physical wider screen
 *     (its layout has multiple seats / fanned hand strip that won't
 *     squeeze into a CSS-rotated portrait canvas), so the player
 *     must actually rotate the device.
 *
 *   • Match → renders the Page normally.
 *
 *   • `orientation: 'any'` (or omitted) → never wraps, never prompts.
 *
 * The hook attempts a hardware orientation lock too (Android Chrome
 * PWA picks it up); when that succeeds, the device physically refuses
 * to rotate and this wrapper never even triggers.
 */
// DevSandboxRoute — fallback for /games/:gameId URLs that don't match
// one of the 5 hardcoded GAMES routes. Used by the 10 locked games
// (Dark Launch sprint) that have a lobby card but no React engine yet.
//
// Access contract: the player must have an active dev-pin grant for
// this game (recorded in the server-side devPinGrants map after a
// successful POST /api/me/games/verify-dev-pin). The lobby API echoes
// the grants in `myDevGrants`, and we read that cached value here so
// a player who types /games/aviator manually with no PIN entered is
// redirected back to the lobby instead of seeing the sandbox.
//
// The server-side WebSocket gate is the real enforcement layer — this
// route guard is just the UX equivalent so the browser doesn't render
// a "you're in" page to someone who isn't.
function DevSandboxRoute() {
  const { gameId } = useParams<{ gameId: string }>();
  const { data, loading } = useLobbyGames();
  if (loading) {
    return (
      <div className="min-h-[60dvh] grid place-items-center text-sm text-arena-500 dark:text-neon-200/55">
        Loading…
      </div>
    );
  }
  if (!data || !gameId) return <Navigate to="/" replace />;
  const game = data.games.find((g) => g.id === gameId);
  if (!game) return <Navigate to="/" replace />;
  // Live games are matched by the specific GAMES.map routes above, so
  // any hit here means the game is either locked OR not yet built.
  // Either way, require a dev grant.
  const hasGrant = data.myDevGrants.includes(gameId);
  if (!hasGrant) return <Navigate to="/" replace />;
  return <DevSandbox />;
}

// Tracks whether the current viewport is desktop-sized (>=1024px).
// Reads the media query synchronously on first render so we don't
// mount the wrong tree, and subscribes to changes so a window-resize
// past the breakpoint flips behaviour without a full reload.
function useIsDesktop(): boolean {
  const [is, setIs] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const fn = () => setIs(mq.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return is;
}

// Phase B (contract B6 / plan §4.4): the games that carry the one-time,
// skippable "Where do you play from?" district ask. This is the set the
// weekly district service actually serves — contract B2 ADRENALINE_GAMES in
// shared/src/games/_shared/adrenalineCore.js (shared tables + solo PvH) —
// listed by id on purpose: the lobby's `tag` is the /games page CATEGORY
// (admin-editable, display only) and files lucky7 under "Jackpot" and rummy
// (a PvP card game) under "Casino", so keying on it would skip the
// highest-volume PvH game and nag on a non-casino one. The player registry
// tag is no better (lucky7 'Casual', plinko/thimbles 'Arcade'). Keep this
// set in step with ADRENALINE_GAMES when a game is added there.
const DISTRICT_ASK_GAMES: ReadonlySet<string> = new Set([
  'aviator', 'andar_bahar', 'teen_patti', 'roulette', // shared tables
  'casino', 'lucky7', 'plinko', 'thimbles',           // solo player-vs-house
]);

function GameRoute({ game }: { game: GameModule }) {
  const required = game.orientation ?? 'any';
  const nav = useNavigate();
  const status = useGameOrientationLock(required);
  const isDesktop = useIsDesktop();
  
  // Security Gate: Enforce Admin "Testing Mode" locks.
  // Even if a game module is shipped in the bundle, if the admin
  // has locked it in the Launch Matrix, it MUST require the Dev PIN.
  const { data, loading } = useLobbyGames();
  
  if (loading) {
    return <div className="min-h-[60dvh] grid place-items-center text-sm text-arena-500 dark:text-neon-200/55">Loading…</div>;
  }
  
  const lobbyDef = data ? data.games.find((g) => g.id === game.id) : undefined;
  if (data && lobbyDef?.isLocked && !data.myDevGrants.includes(game.id)) {
    return <Navigate to="/" replace />;
  }

  // Phase B (contract B6 / plan §4.4): the district ask rides along ONLY on
  // DISTRICT_ASK_GAMES (see above — keyed by game id, not by the display
  // tag). It is a portaled overlay that never blocks the game underneath,
  // so every orientation branch below appends it rather than replacing
  // anything.
  const districtAsk = DISTRICT_ASK_GAMES.has(game.id) ? <DistrictPicker /> : null;

  // Full-screen scrolling games (GameModule.fullscreen, 2026-09-19): the page
  // takes the whole viewport above the NavBar (z-30) and BottomNav (z-40) —
  // like a portrait canvas game — but the frame scrolls, so lists and forms
  // behave as pages. Applied on desktop too: every other game's root is
  // `position: fixed` over the shell anyway, so this is the one consistent
  // "a game owns the screen; its own back button leaves" rule, not a
  // mobile special case. The owner reported Matka King squeezed between the
  // two bars with content hiding under both — this is that fix.
  if (game.fullscreen) {
    return <><div className="game-fullscreen"><game.Page /></div>{districtAsk}</>;
  }

  // 2026-05-22 — Desktop carve-out. On large screens (laptops /
  // desktops) we bypass the orientation system entirely:
  //   • No .force-portrait wrapper — the game renders inside the
  //     natural app shell without any rotation transform.
  //   • No RotatePrompt overlay for landscape games — the device IS
  //     already landscape, so the prompt would be a nonsense modal.
  // This restores the pre-orientation-lock behaviour the user
  // remembered: portrait games just render at their natural layout
  // inside the wide viewport, mobile-first CSS handles the rest.
  if (isDesktop) {
    return <><game.Page />{districtAsk}</>;
  }

  // Portrait game → ALWAYS render the .force-portrait wrapper, even
  // at angle 0. Two reasons:
  //
  // 1. CSS @media queries change synchronously with the viewport, so
  //    putting the rotation activation in CSS — not in React-state —
  //    eliminates the "unrotated landscape" flash that happened when
  //    we conditionally mounted the wrapper. React state updates are
  //    async; CSS rules are not.
  //
  // 2. With `transform: translate(0)` on the wrapper at portrait, it
  //    becomes a containing block for the game's `position: fixed`
  //    root permanently. The containing block never changes during
  //    rotation, which means no layout shift on the transition —
  //    just a transform swap.
  //
  // The `data-angle` attribute is still threaded through so the CSS
  // can distinguish landscape-primary (angle 90, needs -90° rotation)
  // from landscape-secondary (angle 270, needs +90° rotation). The
  // default @media rule below handles the more common direction
  // (angle 90); the override kicks in for 270 / 180.
  if (required === 'portrait') {
    return (
      <>
        <div className="force-portrait" data-angle={status.angle}>
          <game.Page />
        </div>
        {districtAsk}
      </>
    );
  }

  // Landscape game on a portrait device → rotate prompt overlay.
  //
  // Per-game override: if the GameModule supplies its own
  // `RotatePrompt`, render that instead of the platform-default
  // OrientationPrompt. Lets each game preserve its visual identity
  // (Rummy uses a gold-on-emerald felt themed prompt that matches
  // its lobby, defined in player-frontend/src/games/rummy/RotatePrompt.tsx).
  if (required === 'landscape' && status.mismatch) {
    const Prompt = game.RotatePrompt;
    return (
      <>
        <game.Page />
        {Prompt
          ? <Prompt onExit={() => nav('/')} />
          : <OrientationPrompt required="landscape" onExit={() => nav('/')} />}
        {districtAsk}
      </>
    );
  }

  // Match (or 'any') — pass through untouched.
  return <><game.Page />{districtAsk}</>;
}

// Decides whether to render BottomNav. Hidden when:
//  - user is logged out (existing behaviour, BottomNav handles its own null guard)
//  - we're on auth-shell pages (login/register/verify) — keeps the
//    auth UI visually clean. The auth pages already render their own
//    centered card; a sticky bottom nav would visually clutter them.
//
// 2026-05-21 — The "must be verified" gate that used to live here was
// trapping freshly signed-up players: register → auto-login →
// /` lands successfully, but neither email_verified_at nor
// telegram_verified_at is set yet, so this gate returned null and the
// dashboard rendered with no navigation chrome (no way to reach
// Wallet, Profile, etc.). The earlier rewrite already dropped the
// matching verification gate from HomeRoute (verification is now an
// opt-in panel surfaced on /register's welcome screen and inside
// Profile), so leaving the BottomNav gate in place was the last
// hold-out of the deprecated forced-verify flow. Drop it — any
// logged-in player gets the bottom nav.
function BottomNavGate() {
  const { user } = useAuth();
  const loc = useLocation();
  if (!user) return null;
  const hideOnPaths = ['/login', '/register', '/verify', '/verify-email', '/forgot-password', '/reset-password'];
  if (hideOnPaths.includes(loc.pathname)) return null;
  
  // Hide BottomNav when inside a specific Cricket match
  if (loc.pathname.match(/^\/games\/(cricket|football|tennis)\/[a-zA-Z0-9-]+$/)) return null;
  // Full-screen games own the viewport (GameModule.fullscreen) — no bottom nav.
  if (GAMES.some((g) => g.fullscreen && (loc.pathname === `/games/${g.id}` || loc.pathname.startsWith(`/games/${g.id}/`)))) return null;
  
  return <BottomNav />;
}

// Phase R6 (2026-05-13) — Player Geo gate.
//
// Mirrors agent-frontend's needsForcedGeo gate: any logged-in,
// VERIFIED player who is missing precise lat/lng must lock their
// own GPS before they can interact with the rest of the shell.
//
// Two scenarios this triggers in:
//   1. Legacy player (pre-locator signup) — never had lat/lng.
//   2. Admin/parent-agent just edited the player's structured area;
//      the backend invariant (parent-edit-resets-lat/lng) cleared
//      the saved coords so the locator stays coherent. The modal
//      fires on the player's next pageview / login.
//
// Hidden on auth-shell pages — at those pages there's no user yet,
// or the user hasn't completed verification, so the gate is moot.
// `==` (not `===`) on the null check so both `null` and `undefined`
// qualify (the API may surface either depending on driver / JSON
// serialisation quirks).
function GeoGate() {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading || !user) return null;
  const verified = !!(user.email_verified_at || user.telegram_verified_at);
  if (!verified) return null;
  // Stay quiet on auth-shell + verification pages so we don't
  // double up modals when the user is still completing onboarding.
  const hideOnPaths = ['/login', '/register', '/verify', '/verify-email', '/forgot-password', '/reset-password'];
  if (hideOnPaths.includes(loc.pathname)) return null;
  // Phase R6 (2026-05-13) — broadened trigger. Modal fires whenever
  // ANY of the six location fields is missing, not just lat/lng.
  // The Hyper-Local Mesh Network cascade needs every level filled
  // to route coherently (L1 lat/lng + L2-L4 area/district/state +
  // pincode_id as L2's unique routing key). Any null = a hole the
  // server falls through. `==` so null and undefined both qualify.
  // L-6 (2026-08-07): a player is "located" by EITHER precise GPS OR a full
  // state+district+area triple OR a pincode — the SAME rule the Address save
  // gate + the modal submit use. This ends the lockout where a deny-GPS player
  // (or anyone right after an admin area-edit) was trapped behind the
  // non-dismissible modal forever. GPS stays the preferred, more-precise path.
  const needsForcedGeo = !isGeoLocated(user);
  if (!needsForcedGeo) return null;
  return <GeoForcedModal />;
}

export default function App() {
  return (
    <>
      <LanguageSync />
      {/* First-run language hint: NO blocking modal. The top-bar language
          dropdown (LanguageSwitcher) auto-opens once, 3s after load, for
          anonymous visitors who haven't picked a language yet — so they
          discover the option without being walled off. */}
      <div className="min-h-screen min-h-dvh">
        <NavBar />
        <main className="max-w-xl mx-auto px-3 py-4 pb-28">
          <ErrorBoundary><Routes>
            <Route path="/" element={<HomeRoute><HomePage /></HomeRoute>} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            {/* /verify is the post-login gate — Protected (must be
                logged in) but NOT VerifiedRoute (the whole point is
                that the user lands here while still unverified). */}
            <Route path="/verify" element={<Protected><VerifyGatePage /></Protected>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/games" element={<VerifiedRoute><GamesPage /></VerifiedRoute>} />
            {GAMES.map((g) => (
              <Route
                key={g.id}
                path={`/games/${g.id}/*`}
                element={<VerifiedRoute><GameRoute game={g} /></VerifiedRoute>}
              />
            ))}
            {/* Dark Launch sprint (2026-05-21): catch-all for the 10
                locked games whose React engine hasn't shipped yet.
                Only matches URLs not already claimed by GAMES.map
                above (React Router picks specific paths first). The
                wrapper redirects home unless the user has a server-
                side dev-pin grant for this game. */}
            <Route
              path="/games/:gameId"
              element={<VerifiedRoute><DevSandboxRoute /></VerifiedRoute>}
            />
            <Route path="/wallet" element={<VerifiedRoute><WalletPage /></VerifiedRoute>} />
            <Route path="/rewards" element={<VerifiedRoute><RewardsPage /></VerifiedRoute>} />
            <Route path="/profile" element={<VerifiedRoute><ProfilePage /></VerifiedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes></ErrorBoundary>
        </main>
        <BottomNavGate />
        <GeoGate />
        {/* Phase D6 — soft-gate for players who signed up before
            migration 091 and have NULL first_name / last_name /
            birthdate. Dismissible per session; re-opens on next
            mount until the fields are saved. */}
        <CompleteProfileModal />
        <InstallPrompt variant="player" />
      </div>
    </>
  );
}
